/**
 * useTemplateSearch Hook
 *
 * Unified template search that fetches from either firm templates or global templates,
 * with filtering, search, and normalization into a common template type.
 *
 * Uses the same API hooks as AdminTemplatesPage and FirmTemplatesPage.
 */
import { useEffect, useMemo, useState } from 'react';

import { useGetMatterDetailsQuery } from '@/services/api';
import { enhancedApi as adminTemplatesApi } from '@/services/api/generated/adminTemplatesService';
import type { ReadTemplateSummary, TemplateFormat } from '@/services/api/generated/templatesV2Service';
import { useTemplateListQuery } from '@/services/api/generated/templatesV2Service';

export type TemplateType = TemplateFormat;
export type SourceFilter = 'firm' | 'global';

export interface TemplateItem {
  id: string;
  name: string;
  display_name: string | null;
  category: string;
  format: TemplateType;
  is_default: boolean;
  is_firm_template: boolean;
}

export interface UseTemplateSearchOptions {
  matterId: string;
  /** Pre-filter to only show certain categories */
  includeCategories?: string[];
  /** Pre-filter to only show certain template types (e.g., ['PDF'] for merge documents) */
  includeTemplateTypes?: TemplateType[];
}

export interface UseTemplateSearchResult {
  templates: TemplateItem[];
  isLoading: boolean;

  // Filters
  sourceFilter: SourceFilter;
  setSourceFilter: (filter: SourceFilter) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  categoryFilter: string;
  setCategoryFilter: (category: string) => void;

  // Availability flags for UI
  hasFirmTemplates: boolean;
  hasGlobalTemplates: boolean;
  availableCategories: string[];
}

export function useTemplateSearch({
  matterId,
  includeCategories,
  includeTemplateTypes,
}: UseTemplateSearchOptions): UseTemplateSearchResult {
  // Filter state
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('firm');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Get matter to extract firm ID
  const { data: matterData, isLoading: matterLoading } =
    useGetMatterDetailsQuery(matterId, { skip: !matterId });

  const firmId = matterData?.firm?.id;

  // Fetch firm templates (always fetch to detect availability)
  const { data: firmTemplatesData, isLoading: firmLoading } =
    useTemplateListQuery(
      { firmId: firmId || '', scope: 'FIRM', status: 'ACTIVE' },
      { skip: !firmId },
    );
  const firmTemplates = firmTemplatesData?.results ?? [];

  // Fetch global templates (always fetch to detect availability)
  const { data: adminData, isLoading: adminLoading } =
    adminTemplatesApi.useAdminTemplatesListQuery(
      {
        isGlobal: true,
        limit: 500,
        includeDeleted: false,
        status: 'ACTIVE',
      },
      {},
    );

  // Normalize both sources into a common format
  const {
    templates,
    hasFirmTemplates,
    hasGlobalTemplates,
    availableCategories,
  } = useMemo(() => {
    const globalTemplates = adminData?.results ?? [];

    // Normalize firm templates
    const normalizedFirm: TemplateItem[] = firmTemplates.map((t: ReadTemplateSummary) => ({
      id: t.id,
      name: t.name || t.display_name || 'Untitled',
      display_name: t.display_name ?? null,
      category: t.category,
      format: t.format,
      is_default: t.is_default,
      is_firm_template: true,
    }));

    // Normalize global/admin templates
    const normalizedGlobal: TemplateItem[] = globalTemplates.map((t) => ({
      id: t.id,
      name: t.display_name || t.name || 'Untitled',
      display_name: t.display_name ?? null,
      category: t.category,
      format: t.format,
      is_default: t.is_default,
      is_firm_template: false,
    }));

    // Combine for category detection (even if we only show one source)
    const allTemplates = [...normalizedFirm, ...normalizedGlobal];

    // Apply category prefilter
    let prefiltered = allTemplates;
    if (includeCategories && includeCategories.length > 0) {
      prefiltered = prefiltered.filter((t) =>
        includeCategories.includes(t.category),
      );
    }

    // Apply template type prefilter
    if (includeTemplateTypes && includeTemplateTypes.length > 0) {
      prefiltered = prefiltered.filter((t) =>
        includeTemplateTypes.includes(t.format),
      );
    }

    // Detect availability
    const hasFirm = prefiltered.some((t) => t.is_firm_template);
    const hasGlobal = prefiltered.some((t) => !t.is_firm_template);
    const categories = [
      ...new Set(prefiltered.map((t) => t.category)),
    ];

    // Now filter to selected source
    let sourceFiltered = prefiltered.filter((t) =>
      sourceFilter === 'firm' ? t.is_firm_template : !t.is_firm_template,
    );

    // Apply search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      sourceFiltered = sourceFiltered.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.display_name?.toLowerCase().includes(q),
      );
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      sourceFiltered = sourceFiltered.filter(
        (t) => t.category === categoryFilter,
      );
    }

    // Sort: defaults first, then alphabetically
    const sorted = [...sourceFiltered].sort((a, b) => {
      if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
      return a.name.localeCompare(b.name);
    });

    return {
      templates: sorted,
      hasFirmTemplates: hasFirm,
      hasGlobalTemplates: hasGlobal,
      availableCategories: categories,
    };
  }, [
    firmTemplates,
    adminData,
    sourceFilter,
    searchQuery,
    categoryFilter,
    includeCategories,
    includeTemplateTypes,
  ]);

  // Auto-fallback to global templates when firm has none available
  useEffect(() => {
    if (!hasFirmTemplates && hasGlobalTemplates) {
      setSourceFilter('global');
    }
  }, [hasFirmTemplates, hasGlobalTemplates]);

  const isLoading = matterLoading || firmLoading || adminLoading;

  return {
    templates,
    isLoading,
    sourceFilter,
    setSourceFilter,
    searchQuery,
    setSearchQuery,
    categoryFilter,
    setCategoryFilter,
    hasFirmTemplates,
    hasGlobalTemplates,
    availableCategories,
  };
}
